import { c as _c } from "react/compiler-runtime";
import { useAuthContext } from "@context-providers/auth/auth-context";
import { IconButton, UserAvatar, UserMenu as UserMenuDropdown, UserAvatarIcon } from "@flat-ui";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useRouteMatch } from "@hooks/use-route-match";
import { useRedirectToAccountSettingsPage } from "@pages/account-settings/account-settings-utils";
import { AccountSettingsTabs } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { runtimeConfig } from "@src/runtime-config";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector, loggedInUserSelector } from "@store/user/user-selector";
import { UserMenuEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { getPendingUserIcon, getPrettyName, getUserInitials } from "@utils/user-utils";
import { useMemo, useState } from "react";
/**
 * Gets a component that show a user menu to be inserted in the header bar.
 *
 * @returns React user menu component.
 */
export function UserMenu() {
  const $ = _c(90);
  const {
    trackEvent,
    trackAsyncEvent
  } = useTrackEvent();
  const routeIdentifier = useRouteMatch();
  const {
    accountSettingsTab
  } = useAppParams();
  const currentUser = useAppSelector(currentUserSelector);
  const loggedInUser = useAppSelector(loggedInUserSelector);
  const {
    logout
  } = useAuthContext();
  const {
    navigateToRoot,
    navigateToAccountSettingsPage
  } = useAppNavigation();
  const redirectToAccountSettingsPage = useRedirectToAccountSettingsPage();
  const [menuAnchorElement, setMenuAnchorElement] = useState(undefined);
  const isMenuOpen = Boolean(menuAnchorElement);
  let t0;
  if ($[0] !== trackEvent) {
    t0 = {
      text: "Terms of service",
      href: `${runtimeConfig.urls.sphereEntryPageUrl}/legal/#terms-of-service`,
      callback: () => {
        trackEvent({
          name: UserMenuEvents.openTermsDetails
        });
      }
    };
    $[0] = trackEvent;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== trackEvent) {
    t1 = {
      text: "Privacy policy",
      href: `${runtimeConfig.urls.sphereEntryPageUrl}/legal/#privacy-policy`,
      callback: () => {
        trackEvent({
          name: UserMenuEvents.openPrivacyDetails
        });
      }
    };
    $[2] = trackEvent;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== trackEvent) {
    t2 = {
      text: "Imprint",
      href: "https://www.faro.com/de-DE/Imprint",
      callback: () => {
        trackEvent({
          name: UserMenuEvents.openImprintDetails
        });
      }
    };
    $[4] = trackEvent;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = [t0, t1, t2];
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const links = t3;
  let t4;
  bb0: {
    if (currentUser) {
      const t5 = currentUser.id;
      const t6 = currentUser.email;
      const t7 = currentUser.firstName;
      const t8 = currentUser.lastName;
      let t9;
      if ($[10] !== currentUser) {
        t9 = getPrettyName(currentUser);
        $[10] = currentUser;
        $[11] = t9;
      } else {
        t9 = $[11];
      }
      let t10;
      if ($[12] !== currentUser) {
        t10 = getUserInitials(currentUser);
        $[12] = currentUser;
        $[13] = t10;
      } else {
        t10 = $[13];
      }
      const t11 = currentUser.thumbnailUrl;
      let t12;
      if ($[14] !== currentUser) {
        t12 = getPendingUserIcon(currentUser);
        $[14] = currentUser;
        $[15] = t12;
      } else {
        t12 = $[15];
      }
      let t13;
      if ($[16] !== currentUser) {
        t13 = getPendingUserIcon(currentUser, "large");
        $[16] = currentUser;
        $[17] = t13;
      } else {
        t13 = $[17];
      }
      let t14;
      if ($[18] !== currentUser.email || $[19] !== currentUser.firstName || $[20] !== currentUser.id || $[21] !== currentUser.lastName || $[22] !== currentUser.thumbnailUrl || $[23] !== t10 || $[24] !== t12 || $[25] !== t13 || $[26] !== t9) {
        t14 = {
          id: t5,
          email: t6,
          firstName: t7,
          lastName: t8,
          fullName: t9,
          userInitials: t10,
          thumbnailUrl: t11,
          icon: t12,
          largeIcon: t13
        };
        $[18] = currentUser.email;
        $[19] = currentUser.firstName;
        $[20] = currentUser.id;
        $[21] = currentUser.lastName;
        $[22] = currentUser.thumbnailUrl;
        $[23] = t10;
        $[24] = t12;
        $[25] = t13;
        $[26] = t9;
        $[27] = t14;
      } else {
        t14 = $[27];
      }
      t4 = t14;
      break bb0;
    }
    if (loggedInUser) {
      const {
        id,
        firstName,
        lastName,
        email,
        thumbnailUrl
      } = loggedInUser;
      let t5;
      if ($[28] !== firstName || $[29] !== lastName) {
        t5 = getPrettyName({
          firstName,
          lastName
        });
        $[28] = firstName;
        $[29] = lastName;
        $[30] = t5;
      } else {
        t5 = $[30];
      }
      let t6;
      if ($[31] !== firstName || $[32] !== lastName) {
        t6 = getUserInitials({
          firstName,
          lastName
        });
        $[31] = firstName;
        $[32] = lastName;
        $[33] = t6;
      } else {
        t6 = $[33];
      }
      let t7;
      if ($[34] !== email || $[35] !== firstName || $[36] !== id || $[37] !== lastName || $[38] !== t5 || $[39] !== t6 || $[40] !== thumbnailUrl) {
        t7 = {
          id,
          email,
          firstName,
          lastName,
          fullName: t5,
          userInitials: t6,
          thumbnailUrl,
          icon: undefined,
          largeIcon: undefined
        };
        $[34] = email;
        $[35] = firstName;
        $[36] = id;
        $[37] = lastName;
        $[38] = t5;
        $[39] = t6;
        $[40] = thumbnailUrl;
        $[41] = t7;
      } else {
        t7 = $[41];
      }
      t4 = t7;
      break bb0;
    }
    t4 = undefined;
  }
  const userData = t4;
  let t5;
  bb1: {
    if (!userData) {
      t5 = undefined;
      break bb1;
    }
    const t6 = userData.firstName ?? "";
    const t7 = userData.lastName ?? "";
    let t8;
    if ($[42] !== t6 || $[43] !== t7 || $[44] !== userData.email || $[45] !== userData.fullName || $[46] !== userData.thumbnailUrl) {
      t8 = {
        email: userData.email,
        firstName: t6,
        lastName: t7,
        name: userData.fullName,
        profileImageUrl: userData.thumbnailUrl
      };
      $[42] = t6;
      $[43] = t7;
      $[44] = userData.email;
      $[45] = userData.fullName;
      $[46] = userData.thumbnailUrl;
      $[47] = t8;
    } else {
      t8 = $[47];
    }
    t5 = t8;
  }
  const userDisplayInfo = t5;
  let t6;
  if ($[48] !== trackEvent) {
    t6 = function handleClick(event) {
      trackEvent({
        name: UserMenuEvents.openUserMenu
      });
      setMenuAnchorElement(event.currentTarget);
    };
    $[48] = trackEvent;
    $[49] = t6;
  } else {
    t6 = $[49];
  }
  const handleClick = t6;
  let t7;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = function handleClose() {
      setMenuAnchorElement(undefined);
    };
    $[50] = t7;
  } else {
    t7 = $[50];
  }
  const handleClose = t7;
  let t8;
  if ($[51] !== accountSettingsTab || $[52] !== navigateToAccountSettingsPage || $[53] !== redirectToAccountSettingsPage || $[54] !== routeIdentifier || $[55] !== trackAsyncEvent || $[56] !== trackEvent) {
    t8 = function handleAccountSettingsClick(tab) {
      if (routeIdentifier === "account") {
        if (tab === accountSettingsTab) {
          handleClose();
          return;
        } else {
          trackEvent({
            name: UserMenuEvents.openAccountSettings,
            props: {
              from: "user-menu dropdown"
            }
          });
          handleClose();
          navigateToAccountSettingsPage(tab);
          return;
        }
      }
      trackAsyncEvent({
        name: UserMenuEvents.openAccountSettings,
        props: {
          from: "user-menu dropdown"
        }
      });
      handleClose();
      redirectToAccountSettingsPage(tab);
    };
    $[51] = accountSettingsTab;
    $[52] = navigateToAccountSettingsPage;
    $[53] = redirectToAccountSettingsPage;
    $[54] = routeIdentifier;
    $[55] = trackAsyncEvent;
    $[56] = trackEvent;
    $[57] = t8;
  } else {
    t8 = $[57];
  }
  const handleAccountSettingsClick = t8;
  let t9;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = runtimeConfig.appVersion.split("-");
    $[58] = t9;
  } else {
    t9 = $[58];
  }
  const trimmedVersion = t9[0];
  let t10;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      ">:first-of-type": {
        width: "auto",
        height: "auto"
      }
    };
    $[59] = t10;
  } else {
    t10 = $[59];
  }
  let t11;
  if ($[60] !== userDisplayInfo) {
    t11 = userDisplayInfo ? undefined : <UserAvatarIcon />;
    $[60] = userDisplayInfo;
    $[61] = t11;
  } else {
    t11 = $[61];
  }
  let t12;
  if ($[62] !== t11 || $[63] !== userDisplayInfo) {
    t12 = <UserAvatar userDisplayInfo={userDisplayInfo} icon={t11} shouldShowGradientBorder={true} size="s" />;
    $[62] = t11;
    $[63] = userDisplayInfo;
    $[64] = t12;
  } else {
    t12 = $[64];
  }
  let t13;
  if ($[65] !== handleClick || $[66] !== t12) {
    t13 = <IconButton onClick={handleClick} sx={t10}>{t12}</IconButton>;
    $[65] = handleClick;
    $[66] = t12;
    $[67] = t13;
  } else {
    t13 = $[67];
  }
  let t14;
  if ($[68] !== trackEvent) {
    t14 = () => {
      trackEvent({
        name: UserMenuEvents.openLegalDocuments
      });
    };
    $[68] = trackEvent;
    $[69] = t14;
  } else {
    t14 = $[69];
  }
  let t15;
  if ($[70] !== handleAccountSettingsClick) {
    t15 = () => handleAccountSettingsClick(AccountSettingsTabs.security);
    $[70] = handleAccountSettingsClick;
    $[71] = t15;
  } else {
    t15 = $[71];
  }
  let t16;
  if ($[72] !== logout || $[73] !== trackEvent) {
    t16 = () => {
      trackEvent({
        name: UserMenuEvents.logOut,
        props: {
          from: "user-menu dropdown"
        }
      });
      logout();
    };
    $[72] = logout;
    $[73] = trackEvent;
    $[74] = t16;
  } else {
    t16 = $[74];
  }
  let t17;
  if ($[75] !== navigateToRoot || $[76] !== trackEvent) {
    t17 = () => {
      trackEvent({
        name: UserMenuEvents.logIn,
        props: {
          from: "user-menu dropdown"
        }
      });
      navigateToRoot();
    };
    $[75] = navigateToRoot;
    $[76] = trackEvent;
    $[77] = t17;
  } else {
    t17 = $[77];
  }
  let t18;
  if ($[78] !== isMenuOpen || $[79] !== links || $[80] !== menuAnchorElement || $[81] !== t14 || $[82] !== t15 || $[83] !== t16 || $[84] !== t17 || $[85] !== userDisplayInfo) {
    t18 = <UserMenuDropdown anchorElement={menuAnchorElement} userDisplayInfo={userDisplayInfo} open={isMenuOpen} closeMenu={handleClose} appVersion={trimmedVersion} links={links} onOpenLegalDocuments={t14} onSettingsClick={t15} onLogoutClick={t16} onLoginClick={t17} />;
    $[78] = isMenuOpen;
    $[79] = links;
    $[80] = menuAnchorElement;
    $[81] = t14;
    $[82] = t15;
    $[83] = t16;
    $[84] = t17;
    $[85] = userDisplayInfo;
    $[86] = t18;
  } else {
    t18 = $[86];
  }
  let t19;
  if ($[87] !== t13 || $[88] !== t18) {
    t19 = <>{t13}{t18}</>;
    $[87] = t13;
    $[88] = t18;
    $[89] = t19;
  } else {
    t19 = $[89];
  }
  return t19;
}